/* import __COLOCATED_TEMPLATE__ from './sq-sorter.hbs'; */
/**
This component is used for sorting content. It is most often used in table formats, where the content can be
sorted by different keys, and in which key / sort order can be changed by clicking on headers.

It uses contextual components; the `sorter.button` component is [sorter-button](/components/glass/sorter-button.html).

```example
@title Basic Example
@description The objects in the array are originally in this order: 2, -1, 8, 3.<br>
They are sorted into descending order by `sq-sorter`.

@controller-properties
{
  objects: [
    { number: 2 },
    { number: -1 },
    { number: 8 },
    { number: 3 }
  ]
}

@hbs
{{#sq-sorter content=objects sortKey=(desc "number") as |sorter|}}
  {{#each sorter.sortedContent as |object|}}
    {{object.number}}<br>
  {{/each}}
{{/sq-sorter}}
```

```example
@title Example with Sorter Buttons
@description If you click the headers, the content will be resorted.

@controller-properties
{
  people: [
    { firstName: 'Alice', lastName: 'Z' },
    { firstName: 'Bob', lastName: 'Y' },
    { firstName: 'Charlie', lastName: 'X' }
  ]
}

@hbs
{{#sq-sorter content=people
             sortKey="lastName"
             sorterButtonTagName="th"
             as |sorter|}}
  <table>
    <tr>
      {{#sorter.button sortKey="firstName"}}
        First Name
      {{/sorter.button}}
      {{#sorter.button sortKey="lastName"}}
        Last Name
      {{/sorter.button}}
    </tr>
    {{#each sorter.sortedContent as |person|}}
      <tr>
        <td>{{person.firstName}}</td>
        <td>{{person.lastName}}</td>
      </tr>
    {{/each}}
  </table>
{{/sq-sorter}}
```

### Parameters
@param {Object[]} [content] The content to be sorted. The sorted content will be yielded as `sorter.sortedContent`
@param {(String|String[])} [sortKey] The primary key to initially be sorted by. It can also be an array of keys to sort by in that order.
  Each string may also end in `:desc` (using the `desc` helper) to sort by that key in descending order instead.
@param {String[]} [sortDefinition] Holds all keys (primary and secondary) to sort by. This parameter is used by services
  to maintiain sort order between route transitions.
@param {String} [sorterButtonTagName='div'] The tag name that will be given to the yielded `sorter.button`s.
@param {String} [sorterButtonClass=''] A class that will be given to the yielded `sorter.button`s.
@param {Boolean} [sortDefault=true] Whether the sorting should start out sorting in the default order.
@param {Action} [didSort] An action that will receive arguments for the sortKey, sortDefault, and sortDefinition whenever toggleSort is called.
*/

import { sort } from '@ember/object/computed';
import Component from '@ember/component';
import { isArray } from '@ember/array';
import { isNone, isPresent } from '@ember/utils';
import { deepEquals } from '../utils/json-equality';
export function isAscending(sortKey) {
  return sortKeyWithDirection(sortKey).direction === 'asc';
}
export function isDescending(sortKey) {
  return sortKeyWithDirection(sortKey).direction === 'desc';
}
function sortKeyWithDirection(sortKey) {
  if (sortKey.endsWith(':desc')) {
    return {
      sortKey: sortKey.slice(0, sortKey.lastIndexOf(':desc')),
      direction: 'desc'
    };
  } else {
    if (sortKey.endsWith(':asc')) {
      sortKey = sortKey.slice(0, sortKey.lastIndexOf(':asc')); // eslint-disable-line no-param-reassign
    }
    return {
      sortKey,
      direction: 'asc'
    };
  }
}
function reverse(originalSortKey) {
  const {
    sortKey,
    direction
  } = sortKeyWithDirection(originalSortKey);
  const newDirection = direction === 'asc' ? 'desc' : 'asc';
  return `${sortKey}:${newDirection}`;
}
function formatAsArray(key) {
  if (isNone(key)) {
    return [];
  }
  return isArray(key) ? [...key] : [key];
}

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: '',
  content: null,
  sorterButtonTagName: 'div',
  sorterButtonClass: '',
  sortKey: null,
  sortDefault: true,
  sortDefinition: null,
  _sortKey: null,
  // Holds the primary keys to sort by and is used with sortDefault to determine column header caret directions.
  _sortDefault: true,
  // Represents the sort direction of sortKey
  _sortDefinition: null,
  // Holds all keys (primary and secondary) to sort by.

  sortedContent: sort('content', '_sortDefinition'),
  init(...args) {
    this._super(...args);
    this.set('_sortKey', this.get('sortKey'));
    this.set('_sortDefault', this.get('sortDefault'));
    if (isPresent(this.get('sortDefinition'))) {
      this.set('_sortDefinition', this.get('sortDefinition'));
    } else {
      const key = formatAsArray(this.get('sortKey'));
      this.set('_sortDefinition', this.get('_sortDefault') ? key : key.map(reverse));
    }
  },
  actions: {
    toggleSort(sortKey) {
      const newKeys = formatAsArray(sortKey);
      const oldKeys = this.get('_sortDefinition');
      let uniqueKeys;
      if (deepEquals(sortKey, this.get('_sortKey'))) {
        this.toggleProperty('_sortDefault');
        uniqueKeys = [...oldKeys.slice(0, newKeys.length).map(reverse), ...oldKeys.slice(newKeys.length)];
      } else {
        this.setProperties({
          _sortKey: sortKey,
          _sortDefault: true
        });
        uniqueKeys = [...newKeys, ...oldKeys].map(sortKeyWithDirection).reduce((uniqueKeysWithDirection, keyWithDirection) => {
          if (uniqueKeysWithDirection.every(({
            sortKey
          }) => sortKey !== keyWithDirection.sortKey)) {
            uniqueKeysWithDirection.push(keyWithDirection);
          }
          return uniqueKeysWithDirection;
        }, []).map(({
          sortKey,
          direction
        }) => `${sortKey}:${direction}`);
      }
      this.set('_sortDefinition', uniqueKeys);
      if (this.didSort) {
        this.didSort(sortKey, this.get('_sortDefault'), this.get('_sortDefinition'));
      }
    }
  }
});